import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { MachineContext } from '../DataContexts/MachineContext';
import { MachineGroupContext } from '../DataContexts/MachineGroupContext';
import DashboardSection from '../components/DashboardSection';
import {
  FEATURE_KINDS,
  VISIBLE_FEATURES,
  mergeSubOptionCounts,
  optionLabel,
} from './optionalFeatureCatalog';
import '../PlaylistManager/PlaylistAssign.css';
import './OptionalFeaturesBulk.css';

export const SECTION_ANCHOR = 'section-optional-features';
export const FLEET_SCOPE = 'fleet';

const KIND_BADGE = {
  [FEATURE_KINDS.TOGGLE]: 'ON / OFF',
  [FEATURE_KINDS.CHOICE]: 'Options',
};

const KIND_LINE = {
  [FEATURE_KINDS.TOGGLE]: 'On/off feature — a machine is either ON or OFF.',
  [FEATURE_KINDS.CHOICE]: 'Multi-option feature — a machine holds exactly one of the options.',
};

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

// DJB-209: Accounts › Settings › Optional Features. One Config-Subscriptions-
// style section (DJB-179 ps-* design) that bulk-configures optional features
// over the whole fleet or one machine group:
//
// - first column: every optional feature, split into ON/OFF and multi-option
//   features, each with its sub-options and the number of machines currently
//   on each (e.g. Party Shuffle: OFF 3 · 6 2 · 8 5 · 10 2). Clicking a
//   sub-option chip picks that value for the bulk apply.
// - middle column: the selected feature's sub-option table with counts and
//   shares, the value picker and the Apply action (with a confirm step,
//   since applying overwrites the previous setting on every machine in scope).
// - right column: the scope — whole fleet or a machine group. It drives both
//   the counts and the apply target.
//
// Counts come from GET /optional-features/aggregation[?groupId], the apply
// goes through PUT /optional-features/bulk (DJB-Server PR #62), both via
// MachineContext. Groups are NOT expanded client-side here: the backend
// scopes the update itself and answers with the number of machines touched.
const OptionalFeaturesBulk = ({ active = true, onToggle }) => {
  const { machines, fetchOptionalFeatureAggregation, bulkApplyOptionalFeature } =
    useContext(MachineContext);
  const { machineGroups, loading: groupsLoading, error: groupsError } =
    useContext(MachineGroupContext);

  const [scopeGroupId, setScopeGroupId] = useState(null); // null = whole fleet
  const [aggregation, setAggregation] = useState(null); // null until first load
  const [aggregationLoading, setAggregationLoading] = useState(false);
  const [aggregationError, setAggregationError] = useState(null);
  const [selectedFeatureKey, setSelectedFeatureKey] = useState(null);
  const [chosenValue, setChosenValue] = useState(null);
  const [featureSearch, setFeatureSearch] = useState('');
  const [groupSearch, setGroupSearch] = useState('');
  const [confirming, setConfirming] = useState(false);
  // null | { status: 'running' | 'done' | 'failed', featureLabel, valueLabel,
  //          scopeLabel, updatedMachines, message }
  const [applyRun, setApplyRun] = useState(null);
  const applying = applyRun?.status === 'running';

  // Refetch on every open and on every scope change; a request counter drops
  // responses that arrive after the scope moved on.
  const fetchRef = useRef(fetchOptionalFeatureAggregation);
  fetchRef.current = fetchOptionalFeatureAggregation;
  const requestSeq = useRef(0);
  const loadAggregation = async (groupId) => {
    const seq = ++requestSeq.current;
    setAggregationLoading(true);
    setAggregationError(null);
    try {
      const data = await fetchRef.current?.(groupId);
      if (seq !== requestSeq.current) return;
      setAggregation(Array.isArray(data) ? data : []);
    } catch (e) {
      if (seq !== requestSeq.current) return;
      setAggregationError(e.message);
    } finally {
      if (seq === requestSeq.current) setAggregationLoading(false);
    }
  };

  useEffect(() => {
    if (active) loadAggregation(scopeGroupId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, scopeGroupId]);

  const aggregationByFeature = useMemo(() => {
    const map = {};
    (aggregation ?? []).forEach((entry) => {
      if (entry?.feature) map[entry.feature] = entry;
    });
    return map;
  }, [aggregation]);

  const selectedGroup = useMemo(
    () => (scopeGroupId ? machineGroups?.find((g) => g.id === scopeGroupId) ?? null : null),
    [machineGroups, scopeGroupId]
  );

  const scopeLabel = selectedGroup ? `group ${selectedGroup.name}` : 'the whole fleet';

  // Machines in scope: the backend's total (machines that have optional
  // features) once loaded, else the group's member count / the machine list.
  const scopeMachineCount = useMemo(() => {
    const fromAggregation = (aggregation ?? []).find((e) => e?.totalMachines != null)?.totalMachines;
    if (fromAggregation != null) return fromAggregation;
    if (selectedGroup) return selectedGroup.machineIds?.length ?? 0;
    return machines?.length ?? 0;
  }, [aggregation, selectedGroup, machines]);

  const featureRows = useMemo(
    () =>
      VISIBLE_FEATURES.map((feature) => ({
        feature,
        subOptions: mergeSubOptionCounts(feature, aggregationByFeature[feature.key]),
      })),
    [aggregationByFeature]
  );

  const filteredFeatureRows = useMemo(() => {
    const q = featureSearch.toLowerCase().trim();
    if (!q) return featureRows;
    return featureRows.filter(({ feature }) => feature.label.toLowerCase().includes(q));
  }, [featureRows, featureSearch]);

  const toggleRows = filteredFeatureRows.filter(({ feature }) => feature.kind === FEATURE_KINDS.TOGGLE);
  const choiceRows = filteredFeatureRows.filter(({ feature }) => feature.kind === FEATURE_KINDS.CHOICE);

  const selectedRow = featureRows.find(({ feature }) => feature.key === selectedFeatureKey) ?? null;
  const selectedFeature = selectedRow?.feature ?? null;
  const selectedTotal = selectedRow
    ? selectedRow.subOptions.reduce((sum, row) => sum + row.count, 0)
    : 0;

  const filteredGroups = useMemo(() => {
    if (!machineGroups?.length) return [];
    const q = groupSearch.toLowerCase().trim();
    if (!q) return machineGroups;
    return machineGroups.filter((g) => g.name?.toLowerCase().includes(q));
  }, [machineGroups, groupSearch]);

  const selectFeature = (key, value = null) => {
    if (applying) return;
    if (key !== selectedFeatureKey) {
      setSelectedFeatureKey(key);
      setApplyRun(null);
    }
    setChosenValue(value);
  };

  const selectScope = (groupId) => {
    if (applying || groupId === scopeGroupId) return;
    setScopeGroupId(groupId);
    setApplyRun(null);
  };

  const canApply = Boolean(selectedFeature) && chosenValue != null && !applying && !aggregationLoading;

  // Single PUT: the backend scopes the update itself and overwrites the
  // previous value on every machine in scope, then the counts are reloaded.
  const runApply = async () => {
    if (!canApply) return;
    setConfirming(false);
    const run = {
      status: 'running',
      featureLabel: selectedFeature.label,
      valueLabel: optionLabel(selectedFeature, chosenValue),
      scopeLabel,
      updatedMachines: 0,
      message: null,
    };
    setApplyRun(run);
    try {
      const result = await bulkApplyOptionalFeature(selectedFeature.key, chosenValue, scopeGroupId, {
        notify: false,
      });
      setApplyRun({ ...run, status: 'done', updatedMachines: result?.updatedMachines ?? 0 });
      loadAggregation(scopeGroupId);
    } catch (e) {
      setApplyRun({ ...run, status: 'failed', message: e.message });
    }
  };

  const sectionShell = (children) => (
    <DashboardSection
      id={SECTION_ANCHOR}
      title="Optional Features"
      info="Bulk-apply one value of an optional feature to the whole fleet or a machine group."
      isOpen={active}
      onToggle={onToggle}
      bodyId="ofb-body-optional-features"
    >
      <div className="ps-page optional-features-page">{children}</div>
    </DashboardSection>
  );

  if (groupsLoading && !machineGroups?.length) {
    return sectionShell(<p className="ps-status">Loading data, please wait...</p>);
  }

  const renderChips = (feature, subOptions) => (
    <div
      className={`ofb-chips ofb-chips--${feature.kind}`}
      role="group"
      aria-label={`${feature.label} sub-options`}
    >
      {subOptions.map((row) => {
        const isChosen = selectedFeatureKey === feature.key && chosenValue === row.value;
        return (
          <button
            key={row.value}
            type="button"
            className={`ofb-chip${isChosen ? ' is-chosen' : ''}${row.listed ? '' : ' ofb-chip--unlisted'}${
              row.count === 0 ? ' is-empty' : ''
            }`}
            title={
              row.listed
                ? `Pick ${row.label} for ${feature.label}`
                : `${row.label} is not one of the admin options; pick it to keep it as-is`
            }
            aria-label={`${feature.label} ${row.label}: ${plural(row.count, 'machine')}`}
            aria-pressed={isChosen}
            disabled={applying}
            onClick={(e) => {
              e.stopPropagation();
              selectFeature(feature.key, row.value);
            }}
          >
            <span className="ofb-chip-label">{row.label}</span>
            <span className="ofb-chip-count">{aggregationLoading && !aggregation ? '…' : row.count}</span>
          </button>
        );
      })}
    </div>
  );

  const renderFeatureGroup = (title, rows, kind) => (
    <li className={`ofb-feature-group ofb-feature-group--${kind}`} key={kind}>
      <div className="ofb-group-head">
        <span className="ofb-group-label">{title}</span>
        <span className="ps-column-count">{rows.length}</span>
      </div>
      {rows.length === 0 ? (
        <div className="ps-empty">No matching features</div>
      ) : (
        <ul className="ps-playlist-list">
          {rows.map(({ feature, subOptions }) => (
            <li
              key={feature.key}
              className={`ps-playlist-item ofb-feature-item${
                selectedFeatureKey === feature.key ? ' selected' : ''
              }`}
              onClick={() => selectFeature(feature.key, selectedFeatureKey === feature.key ? chosenValue : null)}
            >
              <div className="ps-playlist-row">
                <span className="ps-playlist-name">{feature.label}</span>
                <span className={`ofb-kind-badge ofb-kind-badge--${feature.kind}`}>
                  {KIND_BADGE[feature.kind]}
                </span>
              </div>
              {renderChips(feature, subOptions)}
            </li>
          ))}
        </ul>
      )}
    </li>
  );

  return sectionShell(
    <>
      <div className="ps-columns">
        {/* First column: every optional feature with its sub-option counts */}
        <div className="ps-column ps-column--features">
          <div className="ps-column-head">
            <span className="ps-column-label">Optional features</span>
            <span className="ps-column-count">{filteredFeatureRows.length}</span>
          </div>
          <div className="ps-filters">
            <input
              type="text"
              className="ps-input"
              placeholder="Search by feature name..."
              value={featureSearch}
              onChange={(e) => setFeatureSearch(e.target.value)}
            />
          </div>
          <p className="ofb-scope-note">
            Machine counts for <strong>{scopeLabel}</strong>
            {aggregationLoading ? ' (loading…)' : ''}
          </p>
          {aggregationError && (
            <p className="ps-status ps-status--error" role="alert">
              Error loading counts: {aggregationError}
            </p>
          )}
          <div className="ps-list-scroll">
            <ul className="ofb-feature-groups">
              {renderFeatureGroup('ON / OFF features', toggleRows, FEATURE_KINDS.TOGGLE)}
              {renderFeatureGroup('Multi-option features', choiceRows, FEATURE_KINDS.CHOICE)}
            </ul>
          </div>
        </div>

        {/* Second column: selected feature — sub-options, value picker, apply */}
        <div className="ps-column ps-column--subscribed ofb-column--detail">
          {selectedFeature ? (
            <>
              <div className="ps-selected">
                <div className="ps-selected-row">
                  <div className="ps-selected-name">{selectedFeature.label}</div>
                  <span className={`ofb-kind-badge ofb-kind-badge--${selectedFeature.kind}`}>
                    {KIND_BADGE[selectedFeature.kind]}
                  </span>
                </div>
                <div className="ofb-kind-line">{KIND_LINE[selectedFeature.kind]}</div>
                <div className="ps-selected-id">
                  Feature: <span>{selectedFeature.key}</span>
                </div>
              </div>
              <div className="ps-column-head">
                <h3 className="ps-column-label">Sub-options</h3>
                <span className="ps-column-count">{plural(selectedTotal, 'machine')}</span>
              </div>
              <div className="ps-table-scroll ofb-suboption-scroll">
                <table className="ps-table ofb-suboption-table">
                  <thead>
                    <tr>
                      <th className="ofb-th-pick"></th>
                      <th>Sub-option</th>
                      <th className="ofb-th-count">Machines</th>
                      <th className="ofb-th-share">Share</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedRow.subOptions.map((row) => {
                      const isChosen = chosenValue === row.value;
                      const share = selectedTotal ? Math.round((row.count / selectedTotal) * 100) : 0;
                      const inputId = `ofb-pick-${selectedFeature.key}-${row.value || 'empty'}`;
                      return (
                        <tr
                          key={row.value}
                          className={`ofb-suboption-row${isChosen ? ' is-chosen' : ''}${
                            row.listed ? '' : ' ofb-suboption-row--unlisted'
                          }`}
                          onClick={() => selectFeature(selectedFeature.key, row.value)}
                        >
                          <td className="ofb-td-pick">
                            <input
                              type="radio"
                              id={inputId}
                              name={`ofb-value-${selectedFeature.key}`}
                              value={row.value}
                              checked={isChosen}
                              disabled={applying}
                              onChange={() => selectFeature(selectedFeature.key, row.value)}
                              aria-label={`Pick ${row.label} for ${selectedFeature.label}`}
                            />
                          </td>
                          <td>
                            <label htmlFor={inputId} className="ofb-suboption-label">
                              {selectedFeature.kind === FEATURE_KINDS.TOGGLE && (
                                <span
                                  className={`ofb-switch ofb-switch--${row.value === 'true' ? 'on' : 'off'}`}
                                  aria-hidden="true"
                                />
                              )}
                              {row.label}
                              {!row.listed && <span className="ofb-unlisted-tag">not an admin option</span>}
                            </label>
                          </td>
                          <td className="ofb-td-count">{row.count}</td>
                          <td className="ofb-td-share">
                            <div className="ofb-share" aria-hidden="true">
                              <div className="ofb-share-fill" style={{ width: `${share}%` }} />
                            </div>
                            <span className="ofb-share-text">{share}%</span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="ofb-apply">
                <p className="ofb-apply-warning">
                  Applying overwrites the current {selectedFeature.label} setting on every machine in{' '}
                  {scopeLabel} — each machine ends up on the one value you pick.
                </p>
                <div className="ofb-apply-row">
                  <span className="ofb-apply-summary">
                    {chosenValue == null ? (
                      <>Pick a sub-option to apply.</>
                    ) : (
                      <>
                        Set <strong>{selectedFeature.label}</strong> to{' '}
                        <strong>{optionLabel(selectedFeature, chosenValue)}</strong> on{' '}
                        <strong>{plural(scopeMachineCount, 'machine')}</strong> of {scopeLabel}.
                      </>
                    )}
                  </span>
                  <button
                    type="button"
                    className="ps-btn ps-btn--primary"
                    disabled={!canApply}
                    onClick={() => setConfirming(true)}
                  >
                    {applying ? 'Applying…' : `Apply to ${selectedGroup ? 'group' : 'fleet'}`}
                  </button>
                </div>
              </div>

              {applyRun && (
                <div
                  className={`ofb-result ofb-result--${applyRun.status}`}
                  role="status"
                >
                  {applyRun.status === 'running' && (
                    <p className="ofb-result-text">
                      Applying {applyRun.featureLabel} = {applyRun.valueLabel} to {applyRun.scopeLabel}…
                    </p>
                  )}
                  {applyRun.status === 'done' && (
                    <p className="ofb-result-text">
                      Set {applyRun.featureLabel} to {applyRun.valueLabel} on{' '}
                      {plural(applyRun.updatedMachines, 'machine')} of {applyRun.scopeLabel}.
                    </p>
                  )}
                  {applyRun.status === 'failed' && (
                    <p className="ofb-result-text ofb-result-text--error">
                      Could not set {applyRun.featureLabel} to {applyRun.valueLabel} on {applyRun.scopeLabel}
                      {applyRun.message ? ` — ${applyRun.message}` : ''}. Nothing was changed.
                    </p>
                  )}
                </div>
              )}
            </>
          ) : (
            <div className="ps-placeholder">Select an optional feature to see its sub-options</div>
          )}
        </div>

        {/* Third column: scope — whole fleet or one machine group */}
        <div className="ps-column ps-column--available ofb-column--scope">
          <div className="ps-column-head">
            <h3 className="ps-column-label">Scope</h3>
          </div>
          <p className="ofb-scope-help">
            The counts on the left and the Apply action both follow this scope.
          </p>
          <ul className="ps-playlist-list ofb-scope-list">
            <li
              className={`ps-playlist-item ofb-scope-item${scopeGroupId === null ? ' selected' : ''}`}
              onClick={() => selectScope(null)}
              aria-current={scopeGroupId === null ? 'true' : undefined}
            >
              <div className="ps-playlist-row">
                <span className="ps-playlist-name">Whole fleet</span>
                <span className="ps-subs-badge">
                  {scopeGroupId === null && aggregation
                    ? plural(scopeMachineCount, 'machine')
                    : plural(machines?.length ?? 0, 'machine')}
                </span>
              </div>
            </li>
          </ul>
          <div className="ps-column-head ps-column-head--groups">
            <h3 className="ps-column-label">Machine groups</h3>
            <span className="ps-column-count">{filteredGroups.length}</span>
          </div>
          {groupsError && (
            <p className="ps-status ps-status--error">Error loading groups: {groupsError}</p>
          )}
          <div className="ps-toolbar">
            <input
              type="text"
              className="ps-input"
              placeholder="Search by group name..."
              value={groupSearch}
              onChange={(e) => setGroupSearch(e.target.value)}
            />
          </div>
          <div className="ps-list-scroll ofb-group-scroll">
            <ul className="ps-playlist-list ofb-scope-list">
              {filteredGroups.length === 0 && (
                <li className="ps-empty">{!machineGroups?.length ? 'No groups' : 'No matching groups'}</li>
              )}
              {filteredGroups.map((group) => (
                <li
                  key={group.id}
                  className={`ps-playlist-item ofb-scope-item${scopeGroupId === group.id ? ' selected' : ''}`}
                  onClick={() => selectScope(group.id)}
                  aria-current={scopeGroupId === group.id ? 'true' : undefined}
                >
                  <div className="ps-playlist-row">
                    <span className="ps-playlist-name">{group.name}</span>
                    <span className="ps-subs-badge">
                      {plural(group.machineIds?.length ?? 0, 'machine')}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {confirming && selectedFeature && chosenValue != null && (
        <div className="ps-confirm-overlay" role="presentation">
          <div
            className="ps-confirm-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="ofb-confirm-title"
          >
            <p id="ofb-confirm-title">
              Set <strong>{selectedFeature.label}</strong> to{' '}
              <strong>{optionLabel(selectedFeature, chosenValue)}</strong> on{' '}
              {plural(scopeMachineCount, 'machine')} of {scopeLabel}?
            </p>
            <p className="ofb-confirm-note">
              This overwrites the current {selectedFeature.label} setting on every one of those machines.
            </p>
            <div className="ps-confirm-actions">
              <button type="button" className="ps-btn ps-btn--ghost" onClick={() => setConfirming(false)}>
                Cancel
              </button>
              <button type="button" className="ps-btn ps-btn--primary" onClick={runApply}>
                Apply to {plural(scopeMachineCount, 'machine')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default OptionalFeaturesBulk;
